import { IconCheckCircle } from "@/assets/icons/geist/IconCheckCircle";
import { IconError } from "@/assets/icons/geist/IconError";
import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { IconWarningCircle } from "@/assets/icons/geist/IconWarningCircle";
import { Tooltip } from "@/components/Tooltip";
import { isWarmPoolDeleting, type PlainWarmPool } from "@/queries/warm-pool-queries";
import { WarmPoolPhase } from "gitpod-next-api/gitpod/v1/prebuild_pb";
import type { FC } from "react";

type WarmPoolStatusIconProps = {
    warmPool: PlainWarmPool;
};

function getStatusLabel(phase: WarmPoolPhase): string {
    switch (phase) {
        case WarmPoolPhase.READY:
            return "Ready";
        case WarmPoolPhase.PENDING:
            return "Pending";
        case WarmPoolPhase.DEGRADED:
            return "Degraded";
        case WarmPoolPhase.DELETING:
            return "Deleting";
        case WarmPoolPhase.DELETED:
            return "Deleted";
        default:
            return "Unknown";
    }
}

function getTooltipContent(warmPool: PlainWarmPool): string {
    const deleting = isWarmPoolDeleting(warmPool);

    if (deleting) {
        return "Warm pool is shutting down. Instances are being drained.";
    }

    const phase = warmPool.status?.phase ?? WarmPoolPhase.UNSPECIFIED;
    const label = getStatusLabel(phase);
    const size = warmPool.spec?.desiredSize ?? 0;
    const failureMessage = warmPool.status?.failureMessage;

    let text = `Warm pool: ${label} (size: ${size})`;
    if (failureMessage) {
        text += `\n${failureMessage}`;
    }
    return text;
}

export const WarmPoolStatusIcon: FC<WarmPoolStatusIconProps> = ({ warmPool }) => {
    const deleting = isWarmPoolDeleting(warmPool);
    const phase = warmPool.status?.phase ?? WarmPoolPhase.UNSPECIFIED;
    const tooltipContent = getTooltipContent(warmPool);

    const icon = (() => {
        // Deletion intent takes priority over runner-reported status,
        // since the runner may not have processed it yet.
        if (deleting) {
            return <IconSpinner size="sm" className="animate-spin text-content-secondary" />;
        }

        switch (phase) {
            case WarmPoolPhase.READY:
                return <IconCheckCircle size="sm" className="text-content-green" />;
            case WarmPoolPhase.DEGRADED:
                return <IconWarningCircle size="sm" className="text-content-warning" />;
            case WarmPoolPhase.PENDING:
                return <IconSpinner size="sm" className="animate-spin text-content-secondary" />;
            case WarmPoolPhase.DELETED:
                return <IconError size="sm" className="text-content-tertiary" />;
            default:
                return <IconSpinner size="sm" className="animate-spin text-content-secondary" />;
        }
    })();

    return (
        <Tooltip content={tooltipContent} side="top" className="font-sans" usePortal>
            <span className="inline-flex items-center">{icon}</span>
        </Tooltip>
    );
};
